import React, { useEffect, useState } from 'react';
import { ArrowUp, ArrowDown, ChevronRight, ChevronDown, Info, Database, Download, Upload, Globe, Palette, Sun, Moon, Monitor, Check, Bell, User as UserIcon, Users as UsersIcon } from 'lucide-react';
import { FEATURE_GROUPS, getGroupById } from '../featureRegistry';
import { useTranslation } from '../lib/i18n';
import { useConfirm } from '../lib/ConfirmDialog';
import { useTheme, THEME_COLORS } from '../lib/theme';
import { AVATAR_COLORS, AVATAR_EMOJIS, loadProfile, saveProfile, uploadProfilePhoto } from '../lib/profile';
import {
  isPushSupported,
  getNotificationPermission,
  isCurrentlySubscribed,
  enablePushNotifications,
  disablePushNotifications,
  sendTestPush
} from '../lib/pushNotifications';

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      if (!disabled) onChange();
    }}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-primary-600' : 'bg-gray-300'
    } disabled:opacity-50`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const Settings = ({ data, updateData, importFromLocalStorage, exportData, importData }) => {
  const { t, lang, setLang } = useTranslation();
  const confirm = useConfirm();
  const { mode: themeMode, color: themeColor, setMode: setThemeMode, setColor: setThemeColor } = useTheme();
  const [migrating, setMigrating] = useState(false);
  const [migrateMsg, setMigrateMsg] = useState('');
  const [importStatus, setImportStatus] = useState('');

  // Push-notification state.
  const pushSupported = isPushSupported();
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');
  const [pushPermission, setPushPermission] = useState(
    pushSupported ? getNotificationPermission() : 'denied'
  );

  useEffect(() => {
    let alive = true;
    isCurrentlySubscribed().then((s) => {
      if (alive) setPushSubscribed(s);
    });
    return () => {
      alive = false;
    };
  }, []);

  const [pushTestMsg, setPushTestMsg] = useState('');

  const togglePush = async () => {
    setPushError('');
    setPushTestMsg('');
    setPushBusy(true);
    try {
      if (pushSubscribed) {
        await disablePushNotifications();
        setPushSubscribed(false);
      } else {
        await enablePushNotifications();
        setPushSubscribed(true);
      }
      setPushPermission(getNotificationPermission());
    } catch (err) {
      setPushError(err?.message || 'Failed');
    } finally {
      setPushBusy(false);
    }
  };

  const handleTestPush = async () => {
    setPushError('');
    setPushTestMsg('');
    setPushBusy(true);
    try {
      const res = await sendTestPush();
      setPushTestMsg(
        res.sent > 0
          ? `Sent to ${res.sent} device${res.sent === 1 ? '' : 's'}. Check for the notification.`
          : 'No subscriptions found. Toggle notifications on first.'
      );
      setTimeout(() => setPushTestMsg(''), 6000);
    } catch (err) {
      setPushError(err?.message || 'Failed to send test');
    } finally {
      setPushBusy(false);
    }
  };
  const [profile, setProfile] = useState({
    displayName: '',
    avatarEmoji: '',
    avatarColor: 'blue',
    avatarUrl: '',
    avatarKind: 'emoji'
  });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profileStatus, setProfileStatus] = useState('');

  useEffect(() => {
    let cancelled = false;
    loadProfile()
      .then((p) => {
        if (cancelled || !p) return;
        setProfile(p);
        setProfileLoaded(true);
      })
      .catch(() => setProfileLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist profile changes (debounced) once the initial load has completed.
  useEffect(() => {
    if (!profileLoaded) return;
    const handle = setTimeout(() => {
      saveProfile(profile)
        .then(() => {
          setProfileStatus(t('settings.profileSaved'));
          setTimeout(() => setProfileStatus(''), 1500);
        })
        .catch((err) => setProfileStatus(err.message || 'Save failed'));
    }, 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, profileLoaded]);

  const handleFileImport = async (e) => {
    const file = e.target.files[0];
    if (!file || !importData) return;
    const success = await importData(file);
    setImportStatus(success ? t('settings.importSuccess') : t('settings.importFailed'));
    setTimeout(() => setImportStatus(''), 4000);
    e.target.value = '';
  };

  const hasLocalBackup =
    typeof window !== 'undefined' && !!window.localStorage.getItem('life_manager_data');

  const runMigrate = async () => {
    if (!importFromLocalStorage) return;
    if (!(await confirm({ message: t('settings.confirmMigrate') }))) return;
    setMigrating(true);
    setMigrateMsg('');
    const ok = await importFromLocalStorage();
    setMigrating(false);
    setMigrateMsg(ok ? t('settings.importSuccess') : t('settings.importFailed'));
    setTimeout(() => setMigrateMsg(''), 5000);
  };

  const settings = data.settings || {};
  const enabled = settings.enabledFeatures || {};
  const order = settings.groupOrder || FEATURE_GROUPS.map((g) => g.id);
  const [expanded, setExpanded] = useState({}); // group expanded for description

  const updateSettings = (patch) => {
    updateData({ settings: { ...settings, ...patch } });
  };

  const toggleFeature = (id) => {
    updateSettings({
      enabledFeatures: { ...enabled, [id]: !enabled[id] }
    });
  };

  const moveGroup = (groupId, dir) => {
    const idx = order.indexOf(groupId);
    if (idx === -1) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= order.length) return;
    const next = [...order];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    updateSettings({ groupOrder: next });
  };

  const orderedGroups = order
    .map((id) => getGroupById(id))
    .filter(Boolean);

  const updateProfile = (patch) => setProfile((prev) => ({ ...prev, ...patch }));
  const activeColor = AVATAR_COLORS.find((c) => c.id === profile.avatarColor) || AVATAR_COLORS[0];
  const avatarInitial =
    (profile.displayName || data.profile?.email || '?').trim().charAt(0).toUpperCase() || '?';

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setProfileStatus(t('settings.profilePhotoTooLarge'));
      setTimeout(() => setProfileStatus(''), 3000);
      return;
    }
    setPhotoUploading(true);
    try {
      const url = await uploadProfilePhoto(file);
      updateProfile({ avatarUrl: url, avatarKind: 'photo' });
    } catch (err) {
      setProfileStatus(err.message || 'Upload failed');
      setTimeout(() => setProfileStatus(''), 4000);
    } finally {
      setPhotoUploading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <header>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{t('settings.title')}</h2>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">

      {/* Appearance section */}
      <section className="card">
        <div className="flex items-start gap-3">
          <Palette size={20} className="text-gray-500 dark:text-gray-400 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('settings.appearance')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('settings.appearanceHint')}
            </p>

            {/* Theme mode */}
            <div className="mt-4">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                {t('settings.themeMode')}
              </div>
              <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {[
                  { id: 'light', icon: Sun, label: t('settings.themeLight') },
                  { id: 'dark', icon: Moon, label: t('settings.themeDark') },
                  { id: 'system', icon: Monitor, label: t('settings.themeSystem') }
                ].map((opt, i) => {
                  const active = themeMode === opt.id;
                  const OptIcon = opt.icon;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setThemeMode(opt.id)}
                      className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                        i > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''
                      } ${
                        active
                          ? 'bg-primary-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                      }`}
                    >
                      <OptIcon size={14} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Primary color swatches */}
            <div className="mt-5">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                {t('settings.primaryColor')}
              </div>
              <div className="flex flex-wrap gap-2">
                {THEME_COLORS.map((c) => {
                  const active = themeColor === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setThemeColor(c.id)}
                      title={t(`color.${c.id}`)}
                      aria-label={t(`color.${c.id}`)}
                      className={`relative w-9 h-9 rounded-full border-2 transition-all ${
                        active
                          ? 'border-gray-900 dark:border-gray-100 scale-110 shadow-md'
                          : 'border-gray-200 dark:border-gray-700 hover:scale-105'
                      }`}
                      style={{ backgroundColor: c.swatch }}
                    >
                      {active && (
                        <Check
                          size={16}
                          className={
                            c.id === 'yellow' || c.id === 'white'
                              ? 'text-gray-900 absolute inset-0 m-auto'
                              : 'text-white absolute inset-0 m-auto'
                          }
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notifications section */}
      <section className="card">
        <div className="flex items-start gap-3">
          <Bell size={20} className="text-gray-500 dark:text-gray-400 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Push notifications
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Get reminders for upcoming events, birthdays and shared
                  calendar updates on this device.
                </p>
              </div>
              {pushSupported && (
                <Toggle
                  checked={pushSubscribed}
                  onChange={togglePush}
                  disabled={pushBusy || pushPermission === 'denied'}
                />
              )}
            </div>

            {!pushSupported && (
              <div className="mt-3 text-sm text-amber-700 dark:text-amber-400">
                Your browser doesn’t support push notifications.
                {' '}On iPhone, add Eulea to your home screen first —
                notifications only work from the installed app.
              </div>
            )}

            {pushSupported && pushPermission === 'denied' && (
              <div className="mt-3 text-sm text-amber-700 dark:text-amber-400">
                Notifications are blocked in your browser settings. Allow
                them for this site, then come back and toggle this on.
              </div>
            )}

            {pushError && (
              <div className="mt-3 text-sm text-red-600 dark:text-red-400">
                {pushError}
              </div>
            )}

            {pushSubscribed && (
              <div className="mt-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleTestPush}
                  disabled={pushBusy}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                >
                  Send test notification
                </button>
                {pushTestMsg && (
                  <span className="text-sm text-green-700 dark:text-green-400">{pushTestMsg}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Per-calendar / per-event-type notification preferences. Saved to
          user_settings.notification_prefs and read by the Postgres trigger
          when picking push recipients. */}
      <NotificationPrefsSection
        sharedCalendars={data.calendar?.sharedCalendars || []}
        prefs={settings.notificationPrefs || {}}
        onChange={(prefs) => updateSettings({ notificationPrefs: prefs })}
      />

      {hasLocalBackup && (
        <section className="card border-amber-200 bg-amber-50">
          <div className="flex items-start gap-3">
            <Database size={20} className="text-amber-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-base font-semibold text-amber-900">
                {t('settings.localFound')}
              </h3>
              <p className="text-sm text-amber-800 mt-1">{t('settings.localFoundHint')}</p>
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={runMigrate}
                  disabled={migrating}
                  className="btn-primary text-sm"
                >
                  {migrating ? t('settings.importing') : t('settings.importLocal')}
                </button>
                {migrateMsg && (
                  <span className="text-sm text-amber-900">{migrateMsg}</span>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Reorder section */}
      <section className="card">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {t('settings.menuOrder')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('settings.menuOrderHint')}</p>
        <ul className="divide-y divide-gray-200 dark:divide-gray-800 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
          {orderedGroups.map((group, i) => {
            const Icon = group.icon;
            return (
              <li
                key={group.id}
                className="flex items-center gap-3 px-3 py-2 bg-white dark:bg-gray-900"
              >
                <Icon size={18} className="text-gray-500 dark:text-gray-400" />
                <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200">
                  {t(group.label)}
                </span>
                <button
                  type="button"
                  onClick={() => moveGroup(group.id, -1)}
                  disabled={i === 0}
                  className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 disabled:opacity-30"
                  title={t('settings.moveUp')}
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => moveGroup(group.id, 1)}
                  disabled={i === orderedGroups.length - 1}
                  className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 disabled:opacity-30"
                  title={t('settings.moveDown')}
                >
                  <ArrowDown size={16} />
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Features section */}
      <section className="card">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {t('settings.enabledFeatures')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('settings.enabledFeaturesHint')}</p>

        <div className="space-y-3">
          {orderedGroups.map((group) => {
            const isExpanded = !!expanded[group.id];
            const GroupIcon = group.icon;
            return (
              <div
                key={group.id}
                className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() =>
                    setExpanded((p) => ({ ...p, [group.id]: !p[group.id] }))
                  }
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/60 text-left"
                >
                  <GroupIcon size={18} className="text-gray-500 dark:text-gray-400" />
                  <span className="flex-1 font-medium text-gray-800 dark:text-gray-200">
                    {t(group.label)}
                  </span>
                  {isExpanded ? (
                    <ChevronDown size={16} className="text-gray-400" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/60">
                    {group.description && (
                      <div className="mt-3 flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 rounded p-3">
                        <Info size={14} className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                        <p>{t(group.description)}</p>
                      </div>
                    )}
                    <ul className="mt-3 divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded">
                      {group.items.map((item) => {
                        const ItemIcon = item.icon;
                        // The personal-calendar feature toggle also drives the
                        // legacy `showPersonalCalendar` setting so the sidebar
                        // hides/shows the calendar consistently.
                        const isPersonalCalendar = item.id === 'calendar';
                        const checked = isPersonalCalendar
                          ? enabled.calendar !== false &&
                            settings.showPersonalCalendar !== false
                          : enabled[item.id] !== false;
                        const onToggle = () => {
                          if (isPersonalCalendar) {
                            const next = !checked;
                            updateSettings({
                              showPersonalCalendar: next,
                              enabledFeatures: { ...enabled, calendar: next }
                            });
                          } else {
                            toggleFeature(item.id);
                          }
                        };
                        return (
                          <li
                            key={item.id}
                            className="flex items-start gap-3 px-3 py-2.5"
                          >
                            {ItemIcon && (
                              <ItemIcon size={16} className="text-gray-500 dark:text-gray-400 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                {t(item.label)}
                              </div>
                              {item.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {t(item.description)}
                                </p>
                              )}
                            </div>
                            <Toggle checked={checked} onChange={onToggle} />
                          </li>
                        );
                      })}

                      {/* Shared calendars rendered as extra toggles inside Planung */}
                      {group.id === 'planning' &&
                        (data.calendar?.sharedCalendars || []).map((sc) => {
                          const key = `shared:${sc.id}`;
                          const isVisible = enabled[key] !== false;
                          return (
                            <li
                              key={key}
                              className="flex items-start gap-3 px-3 py-2.5"
                            >
                              <UsersIcon size={16} className="text-gray-500 dark:text-gray-400 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                  {sc.name}
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {sc.isOwner
                                    ? t('sharedCalendar.owner')
                                    : t('sharedCalendar.member')}
                                </p>
                              </div>
                              <Toggle
                                checked={isVisible}
                                onChange={() => toggleFeature(key)}
                              />
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Backup section */}
      <section className="card">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('settings.backup')}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('settings.backupHint')}</p>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={exportData}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-100 text-sm font-medium transition-colors"
          >
            <Download size={16} />
            {t('settings.exportJson')}
          </button>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-100 text-sm font-medium transition-colors cursor-pointer">
            <Upload size={16} />
            {t('settings.importJson')}
            <input type="file" accept=".json" onChange={handleFileImport} className="hidden" />
          </label>
          {importStatus && <span className="text-sm text-gray-700 dark:text-gray-300">{importStatus}</span>}
        </div>
      </section>
        </div>

        {/* Right column: profile */}
        <aside className="space-y-6">
          <section className="card">
            <div className="flex items-start gap-3">
              <UserIcon size={20} className="text-gray-500 dark:text-gray-400 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('settings.profile')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t('settings.profileHint')}
                </p>

                {/* Avatar preview */}
                <div className="mt-4 flex items-center gap-3">
                  {profile.avatarKind === 'photo' && profile.avatarUrl ? (
                    <img
                      src={profile.avatarUrl}
                      alt=""
                      className="w-14 h-14 rounded-full object-cover shadow-sm flex-shrink-0"
                    />
                  ) : (
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-semibold text-white shadow-sm flex-shrink-0"
                      style={{ backgroundColor: activeColor.swatch }}
                    >
                      {profile.avatarEmoji ? (
                        <span className="text-2xl leading-none">{profile.avatarEmoji}</span>
                      ) : (
                        avatarInitial
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                      {profile.displayName || t('settings.profileNoName')}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {profileStatus || t('settings.profileVisibility')}
                    </div>
                  </div>
                </div>

                {/* Display name */}
                <div className="mt-4">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    {t('settings.profileName')}
                  </label>
                  <input
                    type="text"
                    className="input w-full"
                    value={profile.displayName}
                    onChange={(e) => updateProfile({ displayName: e.target.value })}
                    placeholder={t('settings.profileNamePlaceholder')}
                    maxLength={60}
                  />
                </div>

                {/* Avatar kind switch */}
                <div className="mt-5">
                  <div className="inline-flex w-full rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {[
                      { id: 'emoji', label: t('settings.profileKindEmoji') },
                      { id: 'photo', label: t('settings.profileKindPhoto') }
                    ].map((opt, i) => {
                      const active = (profile.avatarKind || 'emoji') === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => updateProfile({ avatarKind: opt.id })}
                          className={`flex-1 px-3 py-1.5 text-sm font-medium transition-colors ${
                            i > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''
                          } ${
                            active
                              ? 'bg-primary-600 text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {profile.avatarKind !== 'photo' ? (
                  <>
                    {/* Avatar color */}
                    <div className="mt-4">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                        {t('settings.profileColor')}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {AVATAR_COLORS.map((c) => {
                          const active = profile.avatarColor === c.id;
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => updateProfile({ avatarColor: c.id })}
                              aria-label={c.id}
                              className={`relative w-8 h-8 rounded-full border-2 transition-all ${
                                active
                                  ? 'border-gray-900 dark:border-gray-100 scale-110 shadow-md'
                                  : 'border-gray-200 dark:border-gray-700 hover:scale-105'
                              }`}
                              style={{ backgroundColor: c.swatch }}
                            >
                              {active && (
                                <Check size={14} className="text-white absolute inset-0 m-auto" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Avatar emoji */}
                    <div className="mt-4">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                        {t('settings.profileEmoji')}
                      </div>
                      <div className="grid grid-cols-8 gap-1">
                        <button
                          type="button"
                          onClick={() => updateProfile({ avatarEmoji: '' })}
                          className={`h-8 rounded-md border text-xs transition ${
                            !profile.avatarEmoji
                              ? 'bg-primary-600 text-white border-primary-600'
                              : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300'
                          }`}
                          title={t('settings.profileEmojiNone')}
                        >
                          Aa
                        </button>
                        {AVATAR_EMOJIS.map((em) => {
                          const active = profile.avatarEmoji === em;
                          return (
                            <button
                              key={em}
                              type="button"
                              onClick={() => updateProfile({ avatarEmoji: em })}
                              className={`h-8 rounded-md border text-lg leading-none transition ${
                                active
                                  ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/30'
                                  : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                              }`}
                            >
                              {em}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  // Photo mode
                  <div className="mt-4 flex flex-col items-start gap-3">
                    <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 text-sm font-medium transition-colors cursor-pointer disabled:opacity-50">
                      <Upload size={16} />
                      {photoUploading
                        ? t('settings.profilePhotoUploading')
                        : profile.avatarUrl
                          ? t('settings.profilePhotoReplace')
                          : t('settings.profilePhotoUpload')}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        disabled={photoUploading}
                        className="hidden"
                      />
                    </label>
                    {profile.avatarUrl && (
                      <button
                        type="button"
                        onClick={() => updateProfile({ avatarUrl: '' })}
                        className="text-xs text-red-600 hover:underline"
                      >
                        {t('settings.profilePhotoRemove')}
                      </button>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('settings.profilePhotoHint')}
                    </p>
                  </div>
                )}

                {/* Language (moved here) */}
                <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-800">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe size={14} className="text-gray-500 dark:text-gray-400" />
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      {t('settings.language')}
                    </div>
                  </div>
                  <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setLang('de')}
                      className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                        lang === 'de'
                          ? 'bg-primary-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                      }`}
                    >
                      {t('settings.languageGerman')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setLang('en')}
                      className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-200 dark:border-gray-700 ${
                        lang === 'en'
                          ? 'bg-primary-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                      }`}
                    >
                      {t('settings.languageEnglish')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};

// =============================================================================
// Per-calendar / per-event-type notification preferences.
//
// Reads/writes the `data.settings.notificationPrefs` JSON which dataSync
// persists into the `user_settings.notification_prefs` column. The Postgres
// trigger in notifications.sql consults the same column when picking push
// recipients, so toggling something here takes effect on the next change
// the OTHER members make in that calendar.
// =============================================================================
const NotificationPrefsSection = ({ sharedCalendars, prefs, onChange }) => {
  const [openId, setOpenId] = useState(null);
  const calendars = sharedCalendars || [];
  const globalMute = !!prefs.globalMute;
  const byCal = prefs.calendars || {};

  const setGlobalMute = (val) => {
    onChange({ ...prefs, globalMute: !!val });
  };

  const setCalPref = (calId, patch) => {
    const prev = byCal[calId] || {};
    const next = { ...prev, ...patch };
    onChange({
      ...prefs,
      calendars: { ...byCal, [calId]: next }
    });
  };

  return (
    <section className="card">
      <div className="flex items-start gap-3">
        <Bell size={20} className="text-gray-500 dark:text-gray-400 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Benachrichtigungs-Einstellungen
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Wähle pro geteiltem Kalender, welche Änderungen dich erreichen
            sollen. Gilt für alle deine Geräte.
          </p>

          {/* Global mute */}
          <div className="mt-4 flex items-center justify-between gap-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                Alle Push-Benachrichtigungen stummschalten
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Übersteuert alles unten.
              </p>
            </div>
            <Toggle checked={globalMute} onChange={() => setGlobalMute(!globalMute)} />
          </div>

          {calendars.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 italic">
              Du bist noch keinen geteilten Kalendern beigetreten.
            </p>
          ) : (
            <ul className="mt-4 divide-y divide-gray-200 dark:divide-gray-800 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
              {calendars.map((cal) => {
                const cp = byCal[cal.id] || {};
                const muted = !!cp.muted;
                const isOpen = openId === cal.id;
                return (
                  <li key={cal.id} className="bg-white dark:bg-gray-900">
                    <div className="flex items-center gap-3 px-3 py-2.5">
                      <button
                        type="button"
                        onClick={() => setOpenId(isOpen ? null : cal.id)}
                        className="flex items-center gap-2 flex-1 min-w-0 text-left"
                        aria-expanded={isOpen}
                      >
                        {isOpen ? (
                          <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                          {cal.name}
                        </span>
                        {muted && (
                          <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-600 dark:text-amber-400 flex-shrink-0">
                            stumm
                          </span>
                        )}
                      </button>
                      <Toggle
                        checked={!muted && !globalMute}
                        disabled={globalMute}
                        onChange={() => setCalPref(cal.id, { muted: !muted })}
                      />
                    </div>
                    {isOpen && (
                      <div className="px-3 pb-3 pt-1 space-y-2 bg-gray-50 dark:bg-gray-900/40">
                        {[
                          { key: 'muteCreated', label: 'Neue Ereignisse' },
                          { key: 'muteUpdated', label: 'Änderungen' },
                          { key: 'muteCommented', label: 'Kommentare' },
                          { key: 'muteDeleted', label: 'Löschungen' }
                        ].map((row) => {
                          const isMuted = !!cp[row.key];
                          return (
                            <div
                              key={row.key}
                              className="flex items-center justify-between gap-3 py-1"
                            >
                              <span className="text-sm text-gray-700 dark:text-gray-200">
                                {row.label}
                              </span>
                              <Toggle
                                checked={!isMuted && !muted && !globalMute}
                                disabled={muted || globalMute}
                                onChange={() =>
                                  setCalPref(cal.id, { [row.key]: !isMuted })
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

export default Settings;
